import { Box } from "@mui/material";
import React, { useContext } from "react";
import ConfigurationContext from "../../../context/Configuration";
import AltNames from "../../MenuName/AltNames";
import QtyStepper from "../Storefront/QtyStepper";
import { BUTTON_RESET, C, FONT_BODY, FOCUS_RING, TYPE } from "../../../utils/designTokens";

const linkButton = {
  ...BUTTON_RESET,
  position: "relative",
  fontFamily: FONT_BODY,
  fontSize: 13,
  fontWeight: 600,
  color: C.redLink,
  textDecoration: "underline",
  textUnderlineOffset: "3px",
  textDecorationColor: "transparent",
  borderRadius: "4px",
  "&:hover": { textDecorationColor: C.redLink },
  // 13px text, 44px tall target.
  "&::before": { content: '""', position: "absolute", inset: "-14px -6px" },
  ...FOCUS_RING,
};

/**
 * Cart line: name, size and unit price, its options, the line total, Edit and
 * Remove links, and a −/+ stepper. Props as before:
 *   foodTitle, variationTitle, unitPrice, optionsWithPrices (preferred) or
 *   legacy optionTitles, specialInstructions, quantity, dealPrice (line total),
 *   addQuantity, removeQuantity, onEdit, onRemove. `as="li"` inside a list.
 *
 * foodAltName1/foodAltName2 + langDisplay show the owner's AI translations in
 * brackets after the name; omitting them renders exactly as before.
 */
function CartItem(props) {
  const configuration = useContext(ConfigurationContext);
  const currency = configuration?.currencySymbol || "$";
  const name = props.foodTitle || "";
  // "Regular" is the size every single-size dish carries; it says nothing.
  const size = props.variationTitle && props.variationTitle !== "Regular" ? props.variationTitle : null;
  // The bag shows what one costs with its options; checkout (no unitPrice)
  // keeps showing the size's own price.
  const unit = props.unitPrice != null
    ? `${currency}${parseFloat(props.unitPrice || 0).toFixed(2)} each`
    : size && props.variationPrice ? `${currency}${props.variationPrice}` : null;

  return (
    <Box component={props.as || "div"} sx={{ listStyle: "none", py: 1.75, borderBottom: `1px solid ${C.border}`, "&:last-of-type": { borderBottom: 0 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ ...TYPE.itemName, color: C.ink, wordBreak: "break-word" }}>
            {name}
            <AltNames alt1={props.foodAltName1} alt2={props.foodAltName2} displayMode={props.langDisplay} />
          </Box>
          {(size || unit) && (
            <Box sx={{ ...TYPE.meta, color: C.muted, mt: 0.25 }}>
              {[size, unit].filter(Boolean).join(" · ")}
            </Box>
          )}

          {/* Options — grouped/indented to match the order summary. Each entry
              may carry `level` (indent), `header` (group name styling) and a
              pre-formatted `title` with its own prefix (+ / •). */}
          {(props?.optionsWithPrices?.length > 0
            ? props.optionsWithPrices.map((option, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 1,
                    pl: (option.level || 0) * 1.25,
                    mt: 0.25,
                    fontFamily: FONT_BODY,
                    fontSize: 13,
                    color: C.muted,
                    fontWeight: option.header ? 600 : 400,
                  }}
                >
                  <span>{option.level === undefined && option.header === undefined ? `+ ${option.title}` : option.title}</span>
                  {option.price ? <span style={{ fontVariantNumeric: "tabular-nums" }}>{`${currency}${option.price}`}</span> : null}
                </Box>
              ))
            : props?.optionTitles?.map((title, index) => (
                <Box key={index} sx={{ mt: 0.25, fontFamily: FONT_BODY, fontSize: 13, color: C.muted }}>
                  {`+ ${title}`}
                </Box>
              )))}

          {props.specialInstructions ? (
            <Box sx={{ mt: 0.75, px: 1, py: 0.5, borderRadius: "8px", bgcolor: C.surface, fontFamily: FONT_BODY, fontSize: 13, color: C.ink, lineHeight: 1.4 }}>
              {`Note: ${props.specialInstructions}`}
            </Box>
          ) : null}
        </Box>
        <Box sx={{ ...TYPE.itemName, color: C.ink, flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
          {`${currency}${parseFloat(props.dealPrice || 0).toFixed(2)}`}
        </Box>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
        <Box sx={{ display: "flex", gap: 2.5, pl: 0.25 }}>
          {props.onEdit && (
            <Box component="button" type="button" onClick={props.onEdit} aria-label={`Edit ${name}`} sx={linkButton}>
              Edit
            </Box>
          )}
          {props.onRemove && (
            <Box component="button" type="button" onClick={props.onRemove} aria-label={`Remove ${name}`} sx={linkButton}>
              Remove
            </Box>
          )}
        </Box>
        <QtyStepper
          quantity={props.quantity ?? 0}
          onAdd={() => props.addQuantity()}
          onRemove={() => props.removeQuantity()}
          itemName={name}
        />
      </Box>
    </Box>
  );
}

export default React.memo(CartItem);
